import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Check, Loader2, Star, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
    {
        id: 'pro_7day',
        name: 'PRO 30-Day',
        price: '$7',
        period: '30 days',
        description: 'Full PRO for 30 days',
        detail: 'Then $47/month',
        savings: null,
        popular: false,
        isTrial: true,
    },
    {
        id: 'pro_monthly',
        name: 'Monthly',
        price: '$47',
        period: '/month',
        description: 'Flexible monthly plan',
        detail: 'Billed monthly',
        savings: null,
        popular: false,
        isTrial: false,
    },
    {
        id: 'pro_3month',
        name: '3 Months',
        price: '$141',
        period: '/3 months',
        description: '$47/month',
        detail: 'Billed every 3 months',
        savings: null,
        popular: false,
        isTrial: false,
    },
    {
        id: 'pro_6month',
        name: '6 Months',
        price: '$235',
        period: '/6 months',
        description: '$39.17/month',
        detail: 'Save $47 (1 month free)',
        savings: '1 Month Free',
        popular: true,
        isTrial: false,
    },
    {
        id: 'pro_12month',
        name: '12 Months',
        price: '$470',
        period: '/year',
        description: '$39.17/month',
        detail: 'Save $94 (2 months free)',
        savings: '2 Months Free',
        popular: false,
        isTrial: false,
    },
];

const Subscription = () => {
    const { token, user } = useAuth();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState(null);
    const [usedTrial, setUsedTrial] = useState(false);

    useEffect(() => {
        // Check if user has already used a trial
        const checkTrial = async () => {
            if (!token) return;
            try {
                const res = await axios.get(`${API_URL}/api/subscription/trial-status`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUsedTrial(res.data.used_trial);
            } catch (err) {
                // ignore
            }
        };
        checkTrial();
    }, [token]);

    const handleSubscribe = async (planId) => {
        setLoading(planId);
        setError(null);
        
        try {
            const response = await axios.post(
                `${API_URL}/api/subscription/create-checkout`,
                { origin_url: window.location.origin, plan: planId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            window.location.href = response.data.checkout_url;
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create checkout');
            setLoading(null);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-16 px-4">
            <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12">
                    <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
                        {language === 'zh' ? '升级至 PRO' : 'Upgrade to PRO'}
                    </h1>
                    <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto">
                        Unlock full access to personalized energy charts, date analysis, and advanced BaZi insights.
                    </p>
                </div>
                
                {error && (
                    <div className="max-w-md mx-auto mb-8 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm text-center">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    {PLANS.map((plan) => (
                        <div
                            key={plan.id}
                            className={`relative rounded-xl border-2 p-5 transition-all ${
                                plan.popular
                                    ? 'border-amber-400 bg-amber-50/50 shadow-lg shadow-amber-100'
                                    : 'border-slate-200 bg-white hover:border-slate-300'
                            } ${(plan.popular || plan.savings) ? 'pt-8' : ''}`}
                            data-testid={`plan-card-${plan.id}`}
                        >
                            {plan.popular && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                                    <Star className="w-3 h-3" /> Best Value
                                </div>
                            )}
                            {plan.savings && !plan.popular && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                                    <Zap className="w-3 h-3" /> {plan.savings}
                                </div>
                            )}
                            
                            <div className="text-center mb-4">
                                <h3 className="font-semibold text-slate-800 text-sm">{plan.name}</h3>
                                <div className="mt-2">
                                    <span className="text-3xl font-bold text-slate-900">{plan.price}</span>
                                    <span className="text-sm text-slate-500 ml-1">{plan.period}</span>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">{plan.description}</p>
                            </div>
                            
                            <p className="text-xs text-center text-slate-500 mb-4">{plan.detail}</p>
                            
                            <Button
                                onClick={() => handleSubscribe(plan.id)}
                                disabled={loading !== null || (plan.isTrial && usedTrial)}
                                className={`w-full text-sm ${
                                    plan.popular
                                        ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                        : ''
                                }`}
                                variant={plan.popular ? 'default' : 'outline'}
                                data-testid={`subscribe-btn-${plan.id}`}
                            >
                                {loading === plan.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : plan.isTrial && usedTrial ? (
                                    'Trial Used'
                                ) : (
                                    'Subscribe'
                                )}
                            </Button>
                        </div>
                    ))}
                </div>

                <div className="mt-12 max-w-md mx-auto">
                    <h3 className="font-semibold text-slate-800 text-center mb-4">All plans include:</h3>
                    <div className="grid grid-cols-2 gap-2">
                        {[
                            'Full Monthly Energy Charts',
                            'Personalized Date Selection',
                            'Advanced BaZi Analysis',
                            'Crimson Horse Insights',
                            'Future Month Forecasts',
                            'Unlimited Access',
                            'Full Features',
                            'Daily Email Forecasts',
                        ].map((feature, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                                <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                <span>{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <p className="text-center text-xs text-slate-400 mt-8">
                    Secure payment via Stripe. Cancel anytime.
                </p>
            </div>
        </div>
    );
};

export default Subscription;
